import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import {
  BarChart3, FileText, Users, GraduationCap, Building2, TrendingUp,
  Download, Eye, Clock, Calendar, AlertTriangle, Shield, PieChart,
  Activity, Target, Layers, Plus, Save, Trash2, Star, Search,
  ChevronRight, ChevronDown, CheckCircle2, Bookmark, Filter, Zap, BookOpen, Heart, Phone,
  UserCheck, MessageSquare, Home, Bell, Percent, Handshake, X, Printer,
  Sparkles, Lock, Columns, Play
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ReportWorkbench,
  exportReportToCsv,
  type OpenReportTab,
  type EscolaItem,
} from './components/relatorios/ReportWorkbench';
import {
  CustomReportBuilder,
  type SavedCustomReport,
  DOMINIOS_DISPONIVEIS,
} from './components/relatorios/CustomReportBuilder';
import './relatorios-municipal.css';

// ─── Types ───
interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  tags: string[];
  estimatedTime?: string;
}

interface SavedReport {
  id: string;
  name: string;
  dimensions: string[];
  metrics: string[];
  filters: string[];
  createdAt: string;
  lastUsed?: string;
  starred?: boolean;
}

// ─── Report Catalog ───
const BASIC_REPORTS: ReportCard[] = [
  { id: 'freq-geral', title: 'Frequência Geral', description: 'Percentual de presença por escola e período', icon: Users, color: '#2563eb', tags: ['frequencia', 'presenca'], estimatedTime: '~5s' },
  { id: 'alunos-escola', title: 'Alunos por Escola', description: 'Quantidade de alunos matriculados em cada unidade', icon: GraduationCap, color: '#7c3aed', tags: ['matricula', 'alunos'], estimatedTime: '~3s' },
  { id: 'turmas-escola', title: 'Turmas por Escola', description: 'Distribuição de turmas, turnos e séries', icon: Building2, color: '#0891b2', tags: ['turmas', 'turnos'], estimatedTime: '~3s' },
  { id: 'professores', title: 'Quadro de Professores', description: 'Professores ativos, carga horária e matérias', icon: BookOpen, color: '#059669', tags: ['professores', 'RH'], estimatedTime: '~4s' },
  { id: 'chamadas-dia', title: 'Chamadas do Dia', description: 'Status das chamadas: realizadas, pendentes e atrasadas', icon: Clock, color: '#d97706', tags: ['chamadas', 'hoje'], estimatedTime: '~2s' },
  { id: 'comunicados', title: 'Comunicados Enviados', description: 'Histórico de comunicados e taxas de leitura', icon: FileText, color: '#dc2626', tags: ['comunicados', 'engajamento'], estimatedTime: '~3s' },
  // ─── Responsáveis ───
  { id: 'resp-cadastro', title: 'Cadastro de Responsáveis', description: 'Total de responsáveis cadastrados, ativos e pendentes por escola', icon: UserCheck, color: '#0d9488', tags: ['responsaveis', 'cadastro', 'ativacao'], estimatedTime: '~4s' },
  { id: 'resp-contato', title: 'Dados de Contato', description: 'Responsáveis com telefone, email e WhatsApp válidos vs sem contato', icon: Phone, color: '#6366f1', tags: ['responsaveis', 'contato', 'whatsapp'], estimatedTime: '~3s' },
  { id: 'resp-vinculo', title: 'Vínculo Aluno-Responsável', description: 'Alunos com/sem responsável vinculado e grau de parentesco', icon: Heart, color: '#ec4899', tags: ['responsaveis', 'vinculo', 'familia'], estimatedTime: '~4s' },
];

const ADVANCED_REPORTS: ReportCard[] = [
  { id: 'evasao', title: 'Risco de Evasão', description: 'Alunos com faltas consecutivas e padrões de ausência críticos', icon: AlertTriangle, color: '#dc2626', tags: ['evasao', 'risco', 'IA'], estimatedTime: '~15s' },
  { id: 'evolucao-freq', title: 'Evolução da Frequência', description: 'Tendência mensal de frequência por escola com comparativo', icon: TrendingUp, color: '#2563eb', tags: ['tendencia', 'historico'], estimatedTime: '~10s' },
  { id: 'desempenho-comp', title: 'Comparativo de Escolas', description: 'Ranking e benchmarking entre escolas da rede', icon: Target, color: '#7c3aed', tags: ['ranking', 'benchmark'], estimatedTime: '~12s' },
  { id: 'perfil-socio', title: 'Perfil Socioeconômico', description: 'Dados de responsáveis, bairros e condições familiares', icon: PieChart, color: '#0891b2', tags: ['social', 'familia'], estimatedTime: '~8s' },
  { id: 'incidentes', title: 'Incidentes e Ocorrências', description: 'Registro de ocorrências por escola, tipo e gravidade', icon: Shield, color: '#ea580c', tags: ['seguranca', 'incidentes'], estimatedTime: '~6s' },
  { id: 'preditivo', title: 'Análise Preditiva', description: 'Projeções de matrícula, evasão e demanda futura com IA', icon: Activity, color: '#8b5cf6', tags: ['IA', 'projecao', 'futuro'], estimatedTime: '~20s' },
  // ─── Responsáveis Avançados ───
  { id: 'resp-engajamento', title: 'Engajamento dos Responsáveis', description: 'Taxa de abertura de comunicados, uso do app e interações por escola', icon: MessageSquare, color: '#0d9488', tags: ['responsaveis', 'engajamento', 'app'], estimatedTime: '~12s' },
  { id: 'resp-ausencia-familia', title: 'Famílias Ausentes', description: 'Responsáveis que nunca acessaram o app ou não responderam comunicados', icon: Home, color: '#dc2626', tags: ['responsaveis', 'risco', 'ausencia'], estimatedTime: '~10s' },
  { id: 'resp-notificacao', title: 'Efetividade das Notificações', description: 'Alertas de presença enviados vs confirmados: WhatsApp, push e email', icon: Bell, color: '#d97706', tags: ['responsaveis', 'notificacao', 'whatsapp'], estimatedTime: '~8s' },
  { id: 'resp-cobertura', title: 'Cobertura Familiar', description: 'Percentual de alunos com responsável ativo no app por escola e turma', icon: Percent, color: '#7c3aed', tags: ['responsaveis', 'cobertura', 'ativacao'], estimatedTime: '~8s' },
  { id: 'resp-participacao', title: 'Participação em Reuniões', description: 'Histórico de presença de responsáveis em reuniões e eventos escolares', icon: Handshake, color: '#059669', tags: ['responsaveis', 'reuniao', 'participacao'], estimatedTime: '~10s' },
  { id: 'resp-correlacao', title: 'Correlação Família x Desempenho', description: 'Cruzamento entre engajamento familiar e notas/frequência dos alunos', icon: Activity, color: '#ec4899', tags: ['responsaveis', 'IA', 'correlacao', 'notas'], estimatedTime: '~18s' },
];

type TabKey = 'basicos' | 'avancados' | 'personalizados';

export function RelatoriosMunicipal() {
  const [activeTab, setActiveTab] = useState<TabKey>('basicos');
  const [searchTerm, setSearchTerm] = useState('');
  const user = useAuthStore((s) => s.user);
  const municipioId = user?.municipioId;
  const userName = user?.nome || user?.email?.split('@')[0] || 'PrefeitoTeste';

  // ─── Lista de Escolas da Rede Municipal ───
  const [escolas, setEscolas] = useState<EscolaItem[]>([]);

  // ─── Multi-Tab Workbench State ───
  const [openTabs, setOpenTabs] = useState<OpenReportTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isSplitMode, setIsSplitMode] = useState<boolean>(false);
  const [secondaryTabId, setSecondaryTabId] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [mobileViewMode, setMobileViewMode] = useState<'catalog' | 'workbench'>('workbench');
  const hasOpenTabs = openTabs.length > 0;

  // Saved custom reports (persistência por município na Galeria)
  const [savedReports, setSavedReports] = useState<SavedCustomReport[]>(() => {
    try {
      const stored = localStorage.getItem('ac_saved_custom_reports_' + (municipioId || 'default'));
      if (stored) return JSON.parse(stored);
      return [
        {
          id: 'custom-tpl-1',
          title: 'Fluxo de Catracas e RFID por Portaria',
          foco: 'foco_rfid' as const,
          dimensoes: ['escola', 'ponto_acesso', 'sentido_passagem'],
          metricas: ['total_passagens', 'passagens_autorizadas', 'acessos_bloqueados', 'tags_ativas'],
          escolaId: 'todas',
          periodo: 'mes_atual',
          formato: 'ranking',
          criadoEm: new Date().toISOString(),
          promptOriginal: 'Gerar relatório de passagens em catracas e cartões RFID',
        },
        {
          id: 'custom-tpl-2',
          title: 'Aparelhos Móveis dos Pais e Entregabilidade Push',
          foco: 'foco_aparelhos' as const,
          dimensoes: ['escola', 'sistema_operacional', 'fabricante_aparelho'],
          metricas: ['total_aparelhos_ativos', 'taxa_entrega_push', 'dispositivos_aprovados'],
          escolaId: 'todas',
          periodo: 'mes_atual',
          formato: 'tabela',
          criadoEm: new Date().toISOString(),
          promptOriginal: 'Mapear smartphones dos pais e push',
        },
      ];
    } catch {
      return [];
    }
  });

  const [customTabMode, setCustomTabMode] = useState<'gallery' | 'builder'>('gallery');
  const [editingReport, setEditingReport] = useState<SavedCustomReport | null>(null);

  const handleSaveCustomReport = (report: SavedCustomReport) => {
    setSavedReports((prev) => {
      const exists = prev.some((r) => r.id === report.id);
      const next = exists ? prev.map((r) => (r.id === report.id ? report : r)) : [report, ...prev];
      try {
        localStorage.setItem(
          'ac_saved_custom_reports_' + (municipioId || 'default'),
          JSON.stringify(next)
        );
      } catch (err) {
        console.error('Erro ao salvar relatório no localStorage:', err);
      }
      return next;
    });
    setCustomTabMode('gallery');
    setEditingReport(null);
    toast.success(`Relatório "${report.title}" salvo na sua Galeria!`);
  };

  const handleDeleteSavedReport = (id: string, title: string) => {
    setSavedReports((prev) => {
      const next = prev.filter((r) => r.id !== id);
      try {
        localStorage.setItem(
          'ac_saved_custom_reports_' + (municipioId || 'default'),
          JSON.stringify(next)
        );
      } catch (err) {
        console.error('Erro ao excluir relatório do localStorage:', err);
      }
      return next;
    });
    toast.info(`Relatório "${title}" excluído da Galeria.`);
  };

  const handleEditSavedReport = (report: SavedCustomReport) => {
    setEditingReport(report);
    setCustomTabMode('builder');
  };

  // Carrega escolas do município para os seletores de filtros
  useEffect(() => {
    if (!municipioId) return;
    const fetchEscolas = async () => {
      try {
        const token = localStorage.getItem('ac_token');
        const base = import.meta.env.VITE_API_URL || '/api/v1';
        const res = await fetch(`${base}/municipios/${municipioId}/escolas`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : data.escolas || [];
          setEscolas(items.map((e: any) => ({ id: e.id, nome: e.nome })));
        }
      } catch (err) {
        console.error('Erro ao carregar escolas:', err);
      }
    };
    fetchEscolas();
  }, [municipioId]);

  const handleGenerate = async (
    report: ReportCard,
    badge?: string,
    initialFilters?: { escolaId?: string; periodo?: string; dataInicio?: string; dataFim?: string }
  ) => {
    if (!municipioId) {
      toast.error('Município não identificado.');
      return;
    }

    const filterEscolaId = initialFilters?.escolaId || 'todas';
    const filterPeriodo = initialFilters?.periodo || 'mes_atual';

    // Se já estiver aberto, foca na aba correspondente
    const existing = openTabs.find(t => t.id === report.id);
    if (existing) {
      setActiveTabId(report.id);
      setMobileViewMode('workbench');
      toast.info(`Relatório "${report.title}" focado na área de análise.`);
      return;
    }

    // Cria nova aba com status loading e filtros iniciais
    const newTab: OpenReportTab = {
      id: report.id,
      title: report.title,
      icon: report.icon,
      color: report.color,
      badge: badge || (BASIC_REPORTS.some(r => r.id === report.id) ? 'Básico' : 'Avançado'),
      data: null,
      loading: true,
      error: null,
      generatedAt: new Date(),
      filterEscolaId,
      filterPeriodo,
      filterDataInicio: initialFilters?.dataInicio,
      filterDataFim: initialFilters?.dataFim,
    };

    setOpenTabs(prev => [...prev, newTab]);
    setActiveTabId(report.id);
    setMobileViewMode('workbench');

    try {
      const qParams = new URLSearchParams();
      if (filterEscolaId && filterEscolaId !== 'todas') qParams.append('escolaId', filterEscolaId);
      if (filterPeriodo) qParams.append('periodo', filterPeriodo);
      if (initialFilters?.dataInicio) qParams.append('dataInicio', initialFilters.dataInicio);
      if (initialFilters?.dataFim) qParams.append('dataFim', initialFilters.dataFim);

      const qs = qParams.toString() ? `?${qParams.toString()}` : '';
      const res = await api.get(`/municipios/${municipioId}/dashboard/relatorios/${report.id}${qs}`);
      const data = res.data;

      setOpenTabs(prev =>
        prev.map(t =>
          t.id === report.id
            ? { ...t, data, loading: false, error: null, generatedAt: new Date() }
            : t
        )
      );
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Erro ao carregar dados';
      setOpenTabs(prev =>
        prev.map(t =>
          t.id === report.id
            ? { ...t, loading: false, error: errorMsg }
            : t
        )
      );
      toast.error(`Erro ao carregar "${report.title}": ${errorMsg}`);
    }
  };

  const handleFilterChange = async (
    tabId: string,
    filters: { escolaId?: string; periodo?: string; dataInicio?: string; dataFim?: string }
  ) => {
    const tab = openTabs.find(t => t.id === tabId);
    if (!tab || !municipioId) return;

    const newEscolaId = filters.escolaId !== undefined ? filters.escolaId : (tab.filterEscolaId || 'todas');
    const newPeriodo = filters.periodo !== undefined ? filters.periodo : (tab.filterPeriodo || 'mes_atual');
    const newDataInicio = filters.dataInicio !== undefined ? filters.dataInicio : tab.filterDataInicio;
    const newDataFim = filters.dataFim !== undefined ? filters.dataFim : tab.filterDataFim;

    setOpenTabs(prev =>
      prev.map(t =>
        t.id === tabId
          ? {
              ...t,
              loading: true,
              error: null,
              filterEscolaId: newEscolaId,
              filterPeriodo: newPeriodo,
              filterDataInicio: newDataInicio,
              filterDataFim: newDataFim,
            }
          : t
      )
    );

    try {
      const qParams = new URLSearchParams();
      if (newEscolaId && newEscolaId !== 'todas') qParams.append('escolaId', newEscolaId);
      if (newPeriodo) qParams.append('periodo', newPeriodo);
      if (newDataInicio) qParams.append('dataInicio', newDataInicio);
      if (newDataFim) qParams.append('dataFim', newDataFim);

      const qs = qParams.toString() ? `?${qParams.toString()}` : '';
      const res = await api.get(`/municipios/${municipioId}/dashboard/relatorios/${tabId}${qs}`);
      const data = res.data;

      setOpenTabs(prev =>
        prev.map(t =>
          t.id === tabId
            ? { ...t, data, loading: false, error: null, generatedAt: new Date() }
            : t
        )
      );
      toast.success(`Filtros aplicados a "${tab.title}".`);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Erro de atualização';
      setOpenTabs(prev =>
        prev.map(t =>
          t.id === tabId
            ? { ...t, loading: false, error: errorMsg }
            : t
        )
      );
      toast.error(`Falha ao aplicar filtros em "${tab.title}".`);
    }
  };

  const handleCloseTab = (id: string) => {
    setOpenTabs(prev => {
      const filtered = prev.filter(t => t.id !== id);
      if (activeTabId === id) {
        const next = filtered[0];
        setActiveTabId(next ? next.id : null);
      }
      if (secondaryTabId === id) {
        const other = filtered.find(t => t.id !== activeTabId);
        setSecondaryTabId(other ? other.id : null);
      }
      if (filtered.length < 2) {
        setIsSplitMode(false);
      }
      if (filtered.length === 0) {
        setMobileViewMode('catalog');
      }
      return filtered;
    });
  };

  const handleCloseAllTabs = () => {
    setOpenTabs([]);
    setActiveTabId(null);
    setSecondaryTabId(null);
    setIsSplitMode(false);
    setIsMaximized(false);
    setMobileViewMode('catalog');
    toast.info('Área de análise limpa.');
  };

  const handleRefreshTab = async (id: string) => {
    const tab = openTabs.find(t => t.id === id);
    if (!tab || !municipioId) return;

    setOpenTabs(prev =>
      prev.map(t => (t.id === id ? { ...t, loading: true, error: null } : t))
    );

    try {
      const token = localStorage.getItem('ac_token');
      const base = import.meta.env.VITE_API_URL || '/api/v1';
      const qParams = new URLSearchParams();
      if (tab.filterEscolaId && tab.filterEscolaId !== 'todas') qParams.append('escolaId', tab.filterEscolaId);
      if (tab.filterPeriodo) qParams.append('periodo', tab.filterPeriodo);
      if (tab.filterDataInicio) qParams.append('dataInicio', tab.filterDataInicio);
      if (tab.filterDataFim) qParams.append('dataFim', tab.filterDataFim);

      const qs = qParams.toString() ? `?${qParams.toString()}` : '';
      const reportEndpoint = id.startsWith('custom-') ? 'desempenho-comp' : id;
      const res = await fetch(`${base}/municipios/${municipioId}/dashboard/relatorios/${reportEndpoint}${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Falha ao atualizar dados.');
      const data = await res.json();

      setOpenTabs(prev =>
        prev.map(t =>
          t.id === id
            ? { ...t, data, loading: false, error: null, generatedAt: new Date() }
            : t
        )
      );
      toast.success(`Relatório "${tab.title}" atualizado.`);
    } catch (err: any) {
      setOpenTabs(prev =>
        prev.map(t =>
          t.id === id
            ? { ...t, loading: false, error: err.message || 'Erro de atualização' }
            : t
        )
      );
      toast.error(`Falha ao atualizar "${tab.title}".`);
    }
  };

  const handleToggleSplitMode = () => {
    if (openTabs.length < 2) {
      toast.info('Abra ao menos 2 relatórios para visualizá-los lado a lado.');
      return;
    }
    setIsSplitMode((prev) => {
      const next = !prev;
      if (next && !secondaryTabId) {
        const other = openTabs.find((t) => t.id !== activeTabId);
        if (other) setSecondaryTabId(other.id);
      }
      return next;
    });
  };

  const handleExecuteCustomReport = async (spec: {
    id: string;
    title: string;
    escolaId: string;
    periodo: string;
    foco?: string;
    dimensoes: string[];
    metricas: string[];
    formato: string;
  }) => {
    const domain = DOMINIOS_DISPONIVEIS.find((d) => d.id === spec.foco) || DOMINIOS_DISPONIVEIS[0];
    const reportCard: ReportCard = {
      id: spec.id || `custom-${Date.now()}`,
      title: spec.title.startsWith('✨') ? spec.title : `✨ ${spec.title}`,
      description: `Foco: ${domain.label} | Dimensões: ${spec.dimensoes.join(', ')} | Métricas: ${spec.metricas.join(', ')}`,
      icon: domain.icon || Sparkles,
      color: domain.color || '#8b5cf6',
      tags: ['personalizado', 'ia', spec.foco || 'custom'],
    };

    handleGenerate(reportCard, 'Personalizado IA', {
      escolaId: spec.escolaId,
      periodo: spec.periodo,
    });
  };

  const filterReports = (reports: ReportCard[]) => {
    if (!searchTerm) return reports;
    const q = searchTerm.toLowerCase();
    return reports.filter(r =>
      r.title.toLowerCase().includes(q) ||
      r.description.toLowerCase().includes(q) ||
      r.tags.some(t => t.includes(q))
    );
  };

  const TABS: { key: TabKey; label: string; icon: any; count: number }[] = [
    { key: 'basicos', label: 'Básicos', icon: FileText, count: BASIC_REPORTS.length },
    { key: 'avancados', label: 'Avançados', icon: Zap, count: ADVANCED_REPORTS.length },
    { key: 'personalizados', label: 'Personalizados', icon: Sparkles, count: savedReports.length },
  ];

  return (
    <div className="admin-page animate-fadeIn rpt-page">
      {/* ═══ HERO STRIP — Central de Relatórios ═══ */}
      <div className="rpt-hero">
        <div className="rpt-hero-circle c1" />
        <div className="rpt-hero-circle c2" />
        <div className="rpt-hero-content">
          <div style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="rpt-hero-badge">
              <BarChart3 size={10} /> INTELIGÊNCIA & DADOS MUNICIPAIS
            </div>
            <h1 className="rpt-hero-title">Central de Relatórios</h1>
            <p className="rpt-hero-subtitle">
              Relatórios analíticos em tempo real, diagnósticos comparativos e exportação de planilhas.
            </p>
          </div>

          <div className="rpt-hero-stats">
            {[
              { icon: FileText, val: BASIC_REPORTS.length, lbl: 'Básicos', clr: '#60a5fa' },
              { icon: Zap, val: ADVANCED_REPORTS.length, lbl: 'Avançados', clr: '#fbbf24' },
              { icon: Sparkles, val: savedReports.length, lbl: 'Personalizados', clr: '#c084fc' },
              { icon: BarChart3, val: BASIC_REPORTS.length + ADVANCED_REPORTS.length, lbl: 'Total Catálogo', clr: '#34d399' },
            ].map((k) => (
              <div key={k.lbl} className="rpt-hero-stat">
                <div className="rpt-hero-stat-icon" style={{ background: `${k.clr}20`, color: k.clr }}>
                  <k.icon size={12} />
                </div>
                <div>
                  <div className="rpt-hero-stat-value">{k.val}</div>
                  <div className="rpt-hero-stat-label">{k.lbl}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ CONTROLE DE MODO MOBILE (Apenas telas pequenas) ═══ */}
      {hasOpenTabs && (
        <div className="rpt-mobile-view-toggle">
          <button
            type="button"
            className={`rpt-mobile-switch-btn ${mobileViewMode === 'catalog' ? 'rpt-mobile-switch-btn-active' : ''}`}
            onClick={() => setMobileViewMode('catalog')}
          >
            <Layers size={13} />
            <span>Catálogo ({openTabs.length} abertos)</span>
          </button>
          <button
            type="button"
            className={`rpt-mobile-switch-btn ${mobileViewMode === 'workbench' ? 'rpt-mobile-switch-btn-active' : ''}`}
            onClick={() => setMobileViewMode('workbench')}
          >
            <BarChart3 size={13} />
            <span>Visualização Ativa</span>
          </button>
        </div>
      )}

      {/* ═══ CONTEÚDO PRINCIPAL (WORKBENCH MULTI-TAB vs CATÁLOGO EXPANDIDO) ═══ */}
      {hasOpenTabs ? (
        <div
          className={`rpt-split-container ${isSplitMode ? 'rpt-split-maximized' : ''} ${
            mobileViewMode === 'workbench' ? 'rpt-split-mobile-workbench' : 'rpt-split-mobile-catalog'
          }`}
        >
          {/* PAINEL LATERAL ESQUERDO: LISTAGEM COMPACTA */}
          {!isSplitMode && (
            <div className="rpt-catalog-panel">
              {/* Barra de Busca Compacta */}
              <div className="rpt-sidebar-search">
                <Search size={12} className="rpt-sidebar-search-icon" />
                <input
                  type="text"
                  placeholder="Filtrar catálogo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rpt-sidebar-search-input"
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="rpt-sidebar-search-clear"
                    onClick={() => setSearchTerm('')}
                  >
                    <X size={10} />
                  </button>
                )}
              </div>

              {/* Tabs de Filtro de Categoria no Sidebar (Aba ativa expandida, inativas apenas ícones) */}
              <div className="rpt-sidebar-tabs">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      className={`rpt-sidebar-tab-btn ${isActive ? 'rpt-sidebar-tab-btn-active' : ''}`}
                      onClick={() => setActiveTab(tab.key)}
                      title={tab.label}
                    >
                      <Icon size={12} />
                      {isActive && <span>{tab.label}</span>}
                      {tab.count >= 0 && (
                        <span className="rpt-sidebar-tab-badge">{tab.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Lista Vertical de Cards no Sidebar */}
              {activeTab === 'basicos' && (
                <div className="rpt-sidebar-cards-list">
                  {filterReports(BASIC_REPORTS).map((r) => {
                    const isOpen = openTabs.some((t) => t.id === r.id);
                    const isActive = activeTabId === r.id;
                    return (
                      <ReportCardItem
                        key={r.id}
                        report={r}
                        onGenerate={(rep) => handleGenerate(rep)}
                        isOpen={isOpen}
                        isActive={isActive}
                      />
                    );
                  })}
                </div>
              )}

              {activeTab === 'avancados' && (
                <div className="rpt-sidebar-cards-list">
                  {filterReports(ADVANCED_REPORTS).map((r) => {
                    const isOpen = openTabs.some((t) => t.id === r.id);
                    const isActive = activeTabId === r.id;
                    return (
                      <ReportCardItem
                        key={r.id}
                        report={r}
                        onGenerate={(rep) => handleGenerate(rep, 'Avançado')}
                        isOpen={isOpen}
                        isActive={isActive}
                      />
                    );
                  })}
                </div>
              )}

              {activeTab === 'personalizados' && (
                <div className="rpt-sidebar-cards-list">
                  <button
                    type="button"
                    className="rpt-sidebar-new-custom-btn"
                    onClick={() => {
                      setEditingReport(null);
                      setCustomTabMode('builder');
                      handleCloseAllTabs();
                    }}
                  >
                    <Sparkles size={13} />
                    <span>+ Criar Novo com IA</span>
                  </button>

                  {savedReports.map((saved) => {
                    const domain =
                      DOMINIOS_DISPONIVEIS.find((d) => d.id === saved.foco) ||
                      DOMINIOS_DISPONIVEIS[0];
                    
                    const cardReport: ReportCard = {
                      id: saved.id,
                      title: saved.title,
                      description: `Foco: ${domain.label} | ${saved.dimensoes.length} dimensões, ${saved.metricas.length} métricas (${saved.periodo})`,
                      icon: domain.icon,
                      color: domain.color,
                      tags: [domain.label, saved.periodo, `${saved.dimensoes.length} dims`, `${saved.metricas.length} métricas`],
                      estimatedTime: '~10s',
                    };

                    const isOpen = openTabs.some((t) => t.id === saved.id);
                    const isActive = activeTabId === saved.id;

                    return (
                      <ReportCardItem
                        key={saved.id}
                        report={cardReport}
                        onGenerate={() => handleExecuteCustomReport(saved)}
                        isOpen={isOpen}
                        isActive={isActive}
                      />
                    );
                  })}
                  {savedReports.length === 0 && (
                    <div className="rpt-empty" style={{ padding: '12px 6px', fontSize: '0.64rem' }}>
                      Nenhum relatório salvo.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PAINEL PRINCIPAL DIREITO: REPORT WORKBENCH */}
          <ReportWorkbench
            openTabs={openTabs}
            activeTabId={activeTabId}
            escolas={escolas}
            onSelectTab={(id) => {
              setActiveTabId(id);
              setMobileViewMode('workbench');
            }}
            onCloseTab={handleCloseTab}
            onCloseAllTabs={handleCloseAllTabs}
            onRefreshTab={handleRefreshTab}
            onFilterChange={handleFilterChange}
            isSplitMode={isSplitMode}
            onToggleSplitMode={handleToggleSplitMode}
            secondaryTabId={secondaryTabId}
            onSelectSecondaryTab={(id) => setSecondaryTabId(id)}
            isMaximized={isMaximized}
            onToggleMaximize={() => setIsMaximized((prev) => !prev)}
            onBackToCatalog={() => setMobileViewMode('catalog')}
          />
        </div>
      ) : (
        /* MODO CATÁLOGO EXPANDIDO / CONSTRUTOR PERSONALIZADO (SEM ABAS ABERTAS) */
        <>
          {/* Toolbar: Tabs + Search */}
          <div className="rpt-toolbar">
            <div className="rpt-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    type="button"
                    className={`rpt-tab ${activeTab === t.key ? 'rpt-tab-active' : ''}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    <Icon size={13} />
                    {t.label}
                    <span className="rpt-tab-badge">{t.count === -1 ? <Sparkles size={10} /> : t.count}</span>
                  </button>
                );
              })}
            </div>

            {activeTab !== 'personalizados' && (
              <div className="rpt-search-wrap">
                <Search size={13} style={{ color: '#64748b', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Buscar relatório..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rpt-search"
                />
              </div>
            )}
          </div>

          {activeTab === 'basicos' && (
            <div className="rpt-grid">
              {filterReports(BASIC_REPORTS).map((r) => (
                <ReportCardItem
                  key={r.id}
                  report={r}
                  onGenerate={(rep) => handleGenerate(rep)}
                />
              ))}
              {filterReports(BASIC_REPORTS).length === 0 && (
                <div className="rpt-empty">Nenhum relatório encontrado.</div>
              )}
            </div>
          )}

          {activeTab === 'avancados' && (
            <div className="rpt-grid">
              {filterReports(ADVANCED_REPORTS).map((r) => (
                <ReportCardItem
                  key={r.id}
                  report={r}
                  onGenerate={(rep) => handleGenerate(rep, 'Avançado')}
                  badge="Avançado"
                />
              ))}
              {filterReports(ADVANCED_REPORTS).length === 0 && (
                <div className="rpt-empty">Nenhum relatório encontrado.</div>
              )}
            </div>
          )}

          {activeTab === 'personalizados' && (
            <>
              {customTabMode === 'builder' || savedReports.length === 0 ? (
                <CustomReportBuilder
                  escolas={escolas}
                  municipioId={municipioId}
                  autorNome={userName}
                  initialReport={editingReport}
                  onBackToGallery={
                    savedReports.length > 0
                      ? () => {
                          setCustomTabMode('gallery');
                          setEditingReport(null);
                        }
                      : undefined
                  }
                  onSaveCustomReport={handleSaveCustomReport}
                  onExecuteCustomReport={handleExecuteCustomReport}
                />
              ) : (
                <div className="rpt-gallery-container">
                  {/* Header da Galeria */}
                  <div className="rpt-gallery-header">
                    <div className="rpt-gallery-title-wrap">
                      <div className="rpt-gallery-icon">
                        <Sparkles size={16} />
                      </div>
                      <div>
                        <h3 className="rpt-gallery-title">
                          Galeria de Relatórios Salvos ({savedReports.length})
                        </h3>
                        <p className="rpt-gallery-subtitle">
                          Modelos personalizados criados pela sua equipe para geração instantânea no Workbench.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="rpt-gallery-new-btn"
                      onClick={() => {
                        setEditingReport(null);
                        setCustomTabMode('builder');
                      }}
                    >
                      <Sparkles size={14} />
                      <span>Criar Novo com IA</span>
                    </button>
                  </div>

                  {/* Grid de Cards Salvos — MESMO PADRÃO DE BÁSICOS E AVANÇADOS */}
                  <div className="rpt-grid">
                    {savedReports.map((saved) => {
                      const domain =
                        DOMINIOS_DISPONIVEIS.find((d) => d.id === saved.foco) ||
                        DOMINIOS_DISPONIVEIS[0];

                      const cardReport: ReportCard = {
                        id: saved.id,
                        title: saved.title,
                        description: `Foco: ${domain.label} | ${saved.dimensoes.length} dimensões, ${saved.metricas.length} métricas (${saved.periodo})`,
                        icon: domain.icon,
                        color: domain.color,
                        tags: [
                          domain.label,
                          saved.escolaId === 'todas'
                            ? 'Todas as Escolas'
                            : escolas.find((e) => e.id === saved.escolaId)?.nome || 'Unidade',
                          saved.periodo,
                          `${saved.dimensoes.length} dims`,
                          `${saved.metricas.length} métricas`,
                        ],
                        estimatedTime: '~10s',
                      };

                      const isOpen = openTabs.some((t) => t.id === saved.id);
                      const isActive = activeTabId === saved.id;

                      return (
                        <ReportCardItem
                          key={saved.id}
                          report={cardReport}
                          onGenerate={() => handleExecuteCustomReport(saved)}
                          isOpen={isOpen}
                          isActive={isActive}
                          author={saved.autorNome || userName}
                          createdAt={saved.criadoEm}
                          onEdit={() => handleEditSavedReport(saved)}
                          onDelete={() => handleDeleteSavedReport(saved.id, saved.title)}
                          badge="Personalizado"
                        />
                      );
                    })}
                    {savedReports.length === 0 && (
                      <div className="rpt-empty">Nenhum relatório salvo.</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Report Card Component (Compacto Horizontal com Detalhes Opcionais) ───
function ReportCardItem({
  report,
  onGenerate,
  badge,
  isOpen,
  isActive,
  author,
  createdAt,
  onEdit,
  onDelete,
}: {
  report: ReportCard;
  onGenerate: (r: ReportCard) => void;
  badge?: string;
  isOpen?: boolean;
  isActive?: boolean;
  author?: string;
  createdAt?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rpt-card-compact ${isActive ? 'rpt-card-compact-active' : ''}`}
      onClick={() => onGenerate(report)}
      title={`Clique para abrir ${report.title}`}
    >
      <div className="rpt-card-compact-row">
        <div
          className="rpt-card-compact-icon"
          style={{ background: `${report.color}15`, color: report.color }}
        >
          <report.icon size={14} />
        </div>

        <div className="rpt-card-compact-title">
          {report.title}
        </div>

        {isOpen && (
          <span
            style={{
              fontSize: '.54rem',
              fontWeight: 700,
              color: isActive ? '#2563eb' : '#16a34a',
              background: isActive ? '#2563eb15' : '#16a34a15',
              padding: '2px 6px',
              borderRadius: 4,
              textTransform: 'uppercase',
              letterSpacing: '.02em',
              flexShrink: 0,
            }}
          >
            {isActive ? 'Ativo' : 'Aberto'}
          </span>
        )}

        <button
          type="button"
          className={`rpt-card-compact-btn ${isActive ? 'rpt-card-compact-btn-active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onGenerate(report);
          }}
          data-tooltip={isOpen ? (isActive ? 'Aba em foco' : 'Alternar para esta aba') : 'Abrir relatório'}
          aria-label="Abrir relatório"
        >
          {isOpen ? <CheckCircle2 size={13} /> : <Eye size={13} />}
        </button>

        <button
          type="button"
          className="rpt-card-compact-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          data-tooltip={expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
          aria-label="Detalhes"
        >
          <ChevronDown
            size={12}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform .15s ease',
            }}
          />
        </button>
      </div>

      {expanded && (
        <div className="rpt-card-details" onClick={(e) => e.stopPropagation()}>
          <p className="rpt-card-details-desc">{report.description}</p>
          <div className="rpt-card-details-meta">
            <div className="rpt-card-tags">
              {report.tags.map(t => (
                <span key={t} className="rpt-tag">{t}</span>
              ))}
            </div>
            {report.estimatedTime && (
              <span className="rpt-card-time" style={{ fontSize: '.55rem' }}>
                <Clock size={9} /> {report.estimatedTime}
              </span>
            )}
            {createdAt && (
              <span className="rpt-card-time" style={{ fontSize: '.55rem' }}>
                <Calendar size={9} /> {new Date(createdAt).toLocaleDateString('pt-BR')} • {new Date(createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {author && (
              <span className="rpt-card-time" style={{ fontSize: '.55rem' }}>
                <Users size={9} /> {author}
              </span>
            )}
          </div>

          {(onEdit || onDelete) && (
            <div className="rpt-card-custom-controls">
              {onEdit && (
                <button
                  type="button"
                  className="rpt-card-custom-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  title="Editar parâmetros no Construtor com IA"
                >
                  <Sparkles size={11} />
                  <span>Editar no Construtor</span>
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="rpt-card-custom-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  title="Remover da Galeria"
                >
                  <Trash2 size={11} />
                  <span>Excluir</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


